import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { UserDivisions } from '@app/@core/constants/user-credentials.constants';
import {
  BUSINESS_DIRECTOR,
  CHAIN,
  MACRO_CHAIN,
  MACRO_OFFICE,
  OFFICE_DIRECTOR,
} from '@app/@core/constants/users.constants';
import { Credentials } from '@app/@core/models/login-models';
import { ResponseDialogComponent } from '@app/@shared/dialogs/response/response-dialog.component';
import { LoaderGenericService } from '@app/@shared/loader-generic/loader-generic.service';
import { TransformNumberPipe } from '@app/@shared/pipes/transform-number.pipe';
import { ExcelService } from '@app/@shared/services/print/excel.service';
import { CombosStore } from '@app/requests/combos-service/combos.store';
import { CommonDataDTO } from '@app/requests/model/combos-model';
import { TranslateService } from '@ngx-translate/core';
import FileSaver from 'file-saver';
import jsPDF, { jsPDFOptions } from 'jspdf';
import autoTable, { Styles, Table } from 'jspdf-autotable';
import { ActivityStore } from '../activity-services/activity.store';
import { MonthlyRequest, MonthlyResult } from '../model/activity-model';
import { RequestComboReports } from '@app/users/model/users-model';

const FORM_PARAM_MACRO_CHAIN = 'macroChain';
const FORM_PARAM_MACRO_OFFICE = 'macroOffice';
const FORM_PARAM_CHAIN = 'chain';
const FORM_PARAM_ESTABLISHMENT = 'establishment';
const FORM_PARAM_PRESCRIPTOR = 'prescriptor';

@Component({
  selector: 'app-macro',
  templateUrl: './macro-current-month.component.html',
  styleUrls: ['./macro-current-month.component.scss'],
})
export class MacroCurrentMonthComponent implements OnInit {
  macroChainList: CommonDataDTO[] = [];
  macroOfficeList: CommonDataDTO[] = [];
  chainsList: CommonDataDTO[];
  prescriptorsList: CommonDataDTO[];
  establishmentsList: CommonDataDTO[];

  searchDataForm: FormGroup;
  resultMonthlyReport: MonthlyResult[];

  isMacroOfficeUser: boolean = false;
  isMacroChainUser: boolean = false;
  isBusinessDirector: boolean = false;

  division: number;

  head: any[] = [];
  private userInfo: Credentials;

  private isChainLoading: boolean = false;
  private isPrescriptorLoading: boolean = false;
  private isEstablishmentsLoading: boolean = false;

  private isCheckAllChains: boolean = false;
  private isCheckAllPrescriber: boolean = false;
  private isCheckAllEstablishments: boolean = false;
  pageSize: number = 15;
  pageIndex: number;
  totalPages: number;
  isGetAll: boolean;

  setCheckAll(check: { id: string; value: boolean }) {
    const { id, value } = check;
    switch (id) {
      case 'chainSelect':
        this.isCheckAllChains = value;
        break;
      case 'prescriptorSelect':
        this.isCheckAllPrescriber = value;
        break;
      case 'establishmentSelect':
        this.isCheckAllEstablishments = value;
        break;
      default:
        console.error('setCheckAll - NOT FOUND:', id);
    }
  }

  constructor(
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private activityStore: ActivityStore,
    private excelService: ExcelService,
    private loaderGenericService: LoaderGenericService,
    private combosStore: CombosStore,
    private translateService: TranslateService,
    private datePipe: DatePipe,
    private transformNumberPipe: TransformNumberPipe
  ) {
    let dataString: string = localStorage.getItem('credentials');
    this.userInfo = JSON.parse(dataString);
  }

  ngOnInit(): void {
    this.division = this.userInfo?.division;
    this.searchDataForm = this.formBuilder.group({
      prescriptor: [''],
      establishment: ['', Validators.required],
    });

    if (!this.isMacroChainUser) {
      this.searchDataForm.addControl(FORM_PARAM_CHAIN, new FormControl(''));
      this.searchDataForm.controls[FORM_PARAM_CHAIN].valueChanges.subscribe((value) => {
        if (value?.length > 0 && !!value[0]) {
          let chainControl = this.searchDataForm.controls[FORM_PARAM_CHAIN];
          if (chainControl?.enabled) {
            if (this.isCheckAllChains) {
              this.loadPrescriptorCombo();
            } else {
              this.loadPrescribersByChains();
            }
          }
        } else this.resetFormControlAndDataList(FORM_PARAM_PRESCRIPTOR);
      });
    }

    this.searchDataForm.controls[FORM_PARAM_PRESCRIPTOR].valueChanges.subscribe((value) => {
      let prescriptorControl = this.searchDataForm.controls.prescriptor;
      if (value?.length > 0 && !!value[0]) {
        if (prescriptorControl?.enabled) {
          if (this.isCheckAllChains && this.isCheckAllPrescriber) {
            this.loadEstablishmentCombo();
          } else {
            this.loadEstablishmentComboByPrescribers();
          }
        }
      } else this.resetFormControlAndDataList(FORM_PARAM_ESTABLISHMENT);
    });

    this.validateMacroChainUser();
    if (this.isBusinessDirector) this.initMacroUsers([FORM_PARAM_MACRO_CHAIN, FORM_PARAM_MACRO_OFFICE]);
    else if (this.isMacroChainUser) this.initMacroUsers([FORM_PARAM_MACRO_CHAIN]);
    else if (this.isMacroOfficeUser) this.initMacroUsers([FORM_PARAM_MACRO_OFFICE]);
    else this.loadChainsCombo();
  }

  onSearchClick(pageIndex: number) {
    this.pageIndex = pageIndex;
    this.resultMonthlyReport = [];
    this.isGetAll = this.isCheckAllChains && this.isCheckAllPrescriber && this.isCheckAllEstablishments;
    const request: MonthlyRequest = {
      isGetAll: this.isGetAll,
      establishmentIdList: !this.isGetAll ? this.searchDataForm.controls.establishment.value : null,
      prescriberIdList: !this.isGetAll ? this.searchDataForm.controls.prescriptor.value : null,
      chainIdList: !this.isGetAll ? this.searchDataForm.controls.chain.value : null,
      officeId:
        this.isMacroOfficeUser || this.isBusinessDirector ? this.searchDataForm.controls?.macroOffice?.value : null,
      chainId:
        this.isMacroChainUser || this.isBusinessDirector ? this.searchDataForm.controls?.macroChain?.value : null,
      pageSize: this.pageSize,
      pageIndex: this.pageIndex,
    };

    const getMonthlyReport: Promise<MonthlyResult[]> =
      this.division === UserDivisions.AUTOS
        ? this.activityStore.getMonthlyAutosReport(request)
        : this.division === UserDivisions.CONSUMPTION
        ? this.activityStore.getMonthlyConsumptionReport(request)
        : null;

    getMonthlyReport
      .then(
        (result) => {
          this.resultMonthlyReport = result ? result : [];
          this.totalPages = this.resultMonthlyReport[0]?.pageTotal ? this.resultMonthlyReport[0]?.pageTotal : 0;
          console.log('resultMonthlyReport', this.resultMonthlyReport);
        },
        (error: any) => {
          if (error.length != 0) {
            console.error(error);
          }
        }
      )
      .catch((error) => console.error(error));
  }

  private buildFilterRequest = (filter?: any) => {
    let request;
    if (filter) request = filter;
    else if (this.isBusinessDirector) {
      request = this.searchDataForm.controls[FORM_PARAM_MACRO_CHAIN].value
        ? { chainId: this.searchDataForm.controls[FORM_PARAM_MACRO_CHAIN].value, userType: CHAIN }
        : { officeId: this.searchDataForm.controls[FORM_PARAM_MACRO_OFFICE].value, userType: OFFICE_DIRECTOR };
    } else if (this.isMacroChainUser)
      request = { chainId: this.searchDataForm.controls[FORM_PARAM_MACRO_CHAIN].value, userType: CHAIN };
    else if (this.isMacroOfficeUser)
      request = { officeId: this.searchDataForm.controls[FORM_PARAM_MACRO_OFFICE].value, userType: OFFICE_DIRECTOR };
    else request = {};
    return request;
  };

  private loadChainsCombo = async (filter?: any) => {
    this.loaderGenericService.showSpinner();
    this.isChainLoading = true;
    this.isPrescriptorLoading = true;
    this.isEstablishmentsLoading = true;
    this.chainsList = [];
    this.combosStore
      .getChains(filter)
      .then(
        (result) => {
          if (result?.length > 0) {
            this.chainsList = result;
            this.setValueMultiselect(FORM_PARAM_CHAIN, this.chainsList);
          } else {
            this.isPrescriptorLoading = false;
            this.isEstablishmentsLoading = false;
          }
        },
        (error) => console.error(error)
      )
      .catch((error) => console.error(error))
      .finally(() => {
        this.isChainLoading = false;
        if (this.isLoadersCompleted()) {
          this.loaderGenericService.stopSpinner();
        }
      });
  };

  private loadPrescriptorCombo = async (filter?: any) => {
    if (!this.isChainLoading) {
      this.loaderGenericService.showSpinner();
    }
    this.isPrescriptorLoading = true;
    this.isEstablishmentsLoading = true;
    this.resetFormControlAndDataList(FORM_PARAM_PRESCRIPTOR);
    const request = this.buildFilterRequest(filter);
    this.combosStore
      .getPrescribers(request)
      .then(
        (result) => {
          if (result?.length > 0) {
            this.prescriptorsList = result;
            this.setValueMultiselect(FORM_PARAM_PRESCRIPTOR, this.prescriptorsList);
          } else this.isEstablishmentsLoading = false;
        },
        (error) => console.error(error)
      )
      .catch((error) => console.error(error))
      .finally(() => {
        this.isPrescriptorLoading = false;
        if (this.isLoadersCompleted()) {
          this.loaderGenericService.stopSpinner();
        }
      });
  };

  private loadEstablishmentCombo = async () => {
    if (!this.isPrescriptorLoading) {
      this.loaderGenericService.showSpinner();
    }
    this.isEstablishmentsLoading = true;
    this.resetFormControlAndDataList(FORM_PARAM_ESTABLISHMENT);
    const request = this.buildFilterRequest();
    this.combosStore
      .getEstablishments(request)
      .then(
        (result) => {
          if (result?.length > 0) {
            this.establishmentsList = result;
            this.setValueMultiselect(FORM_PARAM_ESTABLISHMENT, this.establishmentsList);
          }
        },
        (error) => console.error(error)
      )
      .catch((error) => console.error(error))
      .finally(() => {
        this.isEstablishmentsLoading = false;
        if (this.isLoadersCompleted()) {
          this.loaderGenericService.stopSpinner();
        }
      });
  };

  private loadMacroChains = async () => {
    this.resetFormControlAndDataList(FORM_PARAM_PRESCRIPTOR);
    this.resetFormControlAndDataList(FORM_PARAM_ESTABLISHMENT);
    this.resetFormControlAndDataList(FORM_PARAM_MACRO_CHAIN);
    const promise = this.isBusinessDirector
      ? this.combosStore.getChainsByDivision(this.userInfo.division?.toString())
      : this.combosStore.getChainsFromMacroUser(this.userInfo.userId);
    promise
      .then(
        (data) => {
          if (data) {
            this.macroChainList = [...data];
            if (this.macroChainList.length === 1)
              this.searchDataForm.controls[FORM_PARAM_MACRO_CHAIN].setValue(this.macroChainList[0]?.id);
          }
        },
        (error) => console.error(error)
      )
      .catch((error) => {
        if (error) {
          console.error(error);
          this.showDialogError(error);
        }
      });
  };

  private loadMacroOffices = async () => {
    this.resetFormControlAndDataList(FORM_PARAM_PRESCRIPTOR);
    this.resetFormControlAndDataList(FORM_PARAM_ESTABLISHMENT);
    this.resetFormControlAndDataList(FORM_PARAM_MACRO_OFFICE);
    const promise = this.isBusinessDirector
      ? this.combosStore.getOfficesByDivision(this.userInfo.division?.toString())
      : this.combosStore.getOfficesFromMacroUser(this.userInfo.userId);
    promise
      .then(
        (data) => {
          if (data) {
            this.macroOfficeList = [...data];
            if (this.macroOfficeList.length === 1)
              this.searchDataForm.controls[FORM_PARAM_MACRO_OFFICE].setValue(this.macroOfficeList[0]?.id);
          }
        },
        (error) => console.error(error)
      )
      .catch((error) => {
        if (error) {
          console.error(error);
          this.showDialogError(error);
        }
      });
  };

  private showDialogError(error: any) {
    console.error(error);
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '90%';
    dialogConfig.maxWidth = '450px';
    dialogConfig.data = { text: error?.error?.message, icon: 'icon-ic_circle_cross' };
    this.dialog.open(ResponseDialogComponent, dialogConfig);
  }

  private validateMacroChainUser = () => {
    this.isMacroChainUser = this.userInfo.userType === MACRO_CHAIN;
    this.isMacroOfficeUser = this.userInfo.userType === MACRO_OFFICE;
    this.isBusinessDirector = this.userInfo.userType === BUSINESS_DIRECTOR;
  };

  private initMacroUsers = (formProperties: string[]) => {
    this.disableFormControls();
    formProperties.forEach((property) => {
      const formControl = new FormControl('', Validators.required);
      this.searchDataForm.addControl(property, formControl);
      this.initValueChangesMacroUser(property);
      if (property === FORM_PARAM_MACRO_CHAIN) this.loadMacroChains();
      else if (property === FORM_PARAM_MACRO_OFFICE) this.loadMacroOffices();
    });
  };

  private initValueChangesMacroUser = (property: string) => {
    this.searchDataForm.controls[property].valueChanges.subscribe((value) => {
      if (value) {
        if (property === FORM_PARAM_MACRO_OFFICE) {
          const request = {
            officeId: this.searchDataForm.controls[FORM_PARAM_MACRO_OFFICE].value,
            userType: OFFICE_DIRECTOR,
          };
          this.loadChainsCombo(request);
        } else
          this.loadPrescriptorCombo(property === FORM_PARAM_CHAIN ? { chainId: value, userType: CHAIN } : undefined);

        if (this.isBusinessDirector)
          this.disableFormControl(
            property === FORM_PARAM_MACRO_CHAIN ? FORM_PARAM_MACRO_OFFICE : FORM_PARAM_MACRO_CHAIN
          );
        this.enableFormControls();
      } else {
        this.resetFormControlAndDataList(FORM_PARAM_PRESCRIPTOR);
        if (this.isBusinessDirector)
          this.enableFormControl(
            property === FORM_PARAM_MACRO_CHAIN ? FORM_PARAM_MACRO_OFFICE : FORM_PARAM_MACRO_CHAIN
          );
        this.disableFormControls();
      }
    });
  };

  private enableFormControl = (param: string) => {
    if (param) {
      const form = this.searchDataForm.controls;
      if (form[param]?.disabled) form[param].enable();
    }
  };

  private disableFormControl = (param: string) => {
    if (param) {
      const form = this.searchDataForm.controls;
      if (form[param]?.enabled) {
        if (form[param].value) form[param].setValue('');
        form[param].disable();
      }
    }
  };

  private disableFormControls = () => {
    if (this.searchDataForm) {
      Object.keys(this.searchDataForm.controls).forEach((key) => {
        if (key !== FORM_PARAM_MACRO_CHAIN && key !== FORM_PARAM_MACRO_OFFICE) {
          this.searchDataForm.controls[key].reset();
          if (this.searchDataForm.controls[key].enabled) {
            this.searchDataForm.controls[key].disable();
          }
        }
      });
    }
  };

  private enableFormControls = () => {
    if (this.searchDataForm) {
      Object.keys(this.searchDataForm.controls).forEach((key) => {
        if (
          key !== FORM_PARAM_MACRO_CHAIN &&
          key !== FORM_PARAM_MACRO_OFFICE &&
          this.searchDataForm.controls[key].disabled
        ) {
          if (key === FORM_PARAM_CHAIN) {
            if (this.searchDataForm.controls?.macroChain?.disabled) {
              this.searchDataForm.controls?.chain?.enable();
            } else {
              if (!this.searchDataForm.controls?.macroChain) {
                this.searchDataForm.controls?.chain?.enable();
              }
            }
          } else this.searchDataForm.controls[key].enable();
        }
      });
    }
  };

  private loadEstablishmentComboByPrescribers = async () => {
    this.resetFormControlAndDataList(FORM_PARAM_ESTABLISHMENT);
    const request: RequestComboReports = {
      listPrescribers: this.searchDataForm.controls.prescriptor?.value,
      listChains: this.searchDataForm.controls.chain?.value,
      officeId: this.searchDataForm.controls.macroOffice?.value,
    };
    if (request) {
      this.combosStore
        .getEstablishmentsByPrescribers(request)
        .then(
          (result) => {
            if (result?.length > 0) {
              this.establishmentsList = result;
              this.setValueMultiselect(FORM_PARAM_ESTABLISHMENT, this.establishmentsList);
            }
          },
          (error) => console.log(error)
        )
        .catch((error) => console.log(error));
    }
  };

  private headersPrintExport = (division: number) => {
    return division === UserDivisions.CONSUMPTION
      ? [
          this.translateService.instant('activity.monthly.table.status'),
          this.translateService.instant('activity.monthly.table.cic'),
          this.translateService.instant('activity.monthly.table.tjt'),
          this.translateService.instant('activity.monthly.table.total'),
          this.translateService.instant('activity.monthly.table.finance'),
          this.translateService.instant('activity.monthly.table.prot'),
        ]
      : division === UserDivisions.AUTOS
      ? [
          this.translateService.instant('activity.monthly.table.status'),
          this.translateService.instant('activity.monthly.table.cia'),
          this.translateService.instant('activity.monthly.table.cip'),
          this.translateService.instant('activity.monthly.table.cic'),
          this.translateService.instant('activity.monthly.table.co'),
          this.translateService.instant('activity.monthly.table.lc'),
          this.translateService.instant('activity.monthly.table.total'),
          this.translateService.instant('activity.monthly.table.finance'),
          this.translateService.instant('activity.monthly.table.incTotal'),
          this.translateService.instant('activity.monthly.table.incVendor'),
          this.translateService.instant('activity.monthly.table.ppp'),
          this.translateService.instant('activity.monthly.table.ppb'),
          this.translateService.instant('activity.monthly.table.ppt'),
          this.translateService.instant('activity.monthly.table.protection'),
        ]
      : [];
  };

  private loadPrescribersByChains = async () => {
    this.resetFormControlAndDataList(FORM_PARAM_PRESCRIPTOR);
    const request = {
      listChains: this.searchDataForm.controls[FORM_PARAM_CHAIN].value,
      officeId: this.searchDataForm.controls[FORM_PARAM_MACRO_OFFICE]?.value,
    };
    if (request) {
      this.combosStore.getPrescribersByChains(request).then(
        (result) => {
          if (result?.length > 0) {
            this.prescriptorsList = result;
            this.setValueMultiselect(FORM_PARAM_PRESCRIPTOR, this.prescriptorsList);
          }
        },
        (error) => console.error(error)
      );
    }
  };

  createExcel = () => {
    console.log('createExcel');

    const headersColTable: string[] = this.headersPrintExport(this.division);

    const excelData = {
      headers: headersColTable,
      data: this.resultMonthlyReport,
    };

    this.excelService.exportExcelActivityMonthly(excelData, this.division);
  };

  createPdf = () => {
    const headersColTable: string[] = this.headersPrintExport(this.division);
    try {
      const doc = new jsPDF(this.getjsPDFOptions());
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 5;
      const division =
        this.division === UserDivisions.CONSUMPTION
          ? this.translateService.instant('common.division.consumption')
          : this.userInfo.division === UserDivisions.AUTOS
          ? this.translateService.instant('common.division.auto')
          : '';
      const date = this.datePipe.transform(new Date(), 'ddMMyyyy');
      const filename = this.translateService.instant('monthlyReport.filename.pdf', { division: division, date: date });

      doc.setProperties({
        title: filename,
      });

      const startAutoTableY = 55;
      let lastPosY = startAutoTableY;
      for (let dataItem of this.resultMonthlyReport) {
        const lastAutoTable: Table = (doc as any).lastAutoTable;
        let autoTableY = startAutoTableY;

        if (lastAutoTable) {
          autoTableY = lastAutoTable.finalY + margin;
          if (lastAutoTable.finalY > pageHeight - margin * 5) {
            doc.addPage();
            autoTableY = startAutoTableY;
          }
        }
        doc.setFontSize(12);
        doc.text(dataItem.agentPk, margin, autoTableY + 2);

        const body: any[] =
          UserDivisions.AUTOS === this.division
            ? dataItem.statusAuto?.map((status) => [
                status?.state,
                status?.p20,
                status?.p15,
                status?.p10,
                status?.p30,
                status?.p40,
                status?.total,
                this.transformNumberPipe.transform(status?.financed),
                this.transformNumberPipe.transform(status?.incAgent),
                this.transformNumberPipe.transform(status?.incSeller),
                status?.losses,
                status?.ppb,
                status?.ppt,
                this.transformNumberPipe.transform(status?.incProtections),
              ])
            : dataItem.statusConsumption?.map((status) => [
                status?.state,
                status?.p10,
                status?.p30,
                status?.total,
                this.transformNumberPipe.transform(status?.financed),
                status?.ppt,
              ]);

        autoTable(doc, {
          startY: autoTableY + margin,
          columnStyles: this.colsStyle(pageWidth),
          styles: {
            fontSize: 8,
          },
          rowPageBreak: 'avoid',
          showHead: 'everyPage',
          margin: { top: startAutoTableY, left: margin, bottom: 0, right: margin },
          body: body,
          didDrawCell: (data) => {
            const cell = data.cell;
            const width = cell.width;
            const height = cell.height;
            doc.rect(cell.x, cell.y, width, height);
          },
          didDrawPage: (data) => {
            if (autoTableY <= lastPosY) {
              //First Row
              const img = new Image();
              img.src = 'assets/img/logo.PNG';
              doc.addImage(img, 'png', 0, 0, 80, 30);
              doc.setFontSize(20);
              doc.setTextColor('1E90FF');
              doc.text(this.translateService.instant('sidenav.menu.reports'), 100, 18);
              //Second Row
              const secondRowY = 35;
              doc.setTextColor('#000000');
              doc.setFontSize(12);
              doc.text(this.translateService.instant('sidenav.menu.reports.current-month'), margin, secondRowY);
              doc.setFontSize(10);
              const currentDate = this.datePipe.transform(new Date().toString(), 'dd-MM-YYYY HH:mm:ss');
              const currentDateX = pageWidth - (doc.getTextWidth(currentDate) + margin);
              doc.text(currentDate, currentDateX, secondRowY);
              //Third row
              const squareY = 40;
              const squareH = 10;
              const thirdRowY = 46;
              if (this.division === UserDivisions.CONSUMPTION) {
                doc.setFontSize(10);
                let lastMaxWidthCell = margin;
                const numCols = 10;
                for (let i = 0; i < headersColTable.length; i++) {
                  const textWidth = doc.getTextWidth(headersColTable[i]);
                  const colSize = i === 0 ? 4 : i === 4 ? 2 : 1;
                  const maxWidthCell = ((pageWidth - margin * 2) / numCols) * colSize;
                  const textPosXCell = lastMaxWidthCell + (maxWidthCell - textWidth) / 2;
                  doc.setFillColor('#cccccc');
                  doc.setTextColor('#FFFFFF');
                  doc.rect(lastMaxWidthCell, squareY, maxWidthCell, squareH, 'FD');
                  doc.text(headersColTable[i], textPosXCell, thirdRowY);
                  lastMaxWidthCell = lastMaxWidthCell + maxWidthCell;
                }
              } else {
                doc.setFontSize(8);
                let lastMaxWidthCell = margin;
                const numCols = 20;
                for (let i = 0; i < headersColTable.length; i++) {
                  const textWidth = doc.getTextWidth(headersColTable[i]);
                  const colSize = i === 0 ? 3 : [7, 8, 9, 13].includes(i) ? 2 : 1;
                  const maxWidthCell = ((pageWidth - margin * 2) / numCols) * colSize;
                  const textPosXCell = lastMaxWidthCell + (maxWidthCell - textWidth) / 2;
                  doc.setFillColor('#cccccc');
                  doc.setTextColor('#FFFFFF');
                  doc.rect(lastMaxWidthCell, squareY, maxWidthCell, squareH, 'FD');
                  doc.text(headersColTable[i], textPosXCell, thirdRowY);
                  lastMaxWidthCell = lastMaxWidthCell + maxWidthCell;
                }
              }
            }
            lastPosY = autoTableY;
          },
        });
      }

      let blob = doc.output('blob');
      FileSaver.saveAs(blob, filename);
      window.open(URL.createObjectURL(blob));
    } catch (error) {
      console.error(error);
    }
  };

  private getjsPDFOptions = (): jsPDFOptions => {
    let options: jsPDFOptions = {
      orientation: 'portrait',
      format: 'a4',
    };
    if (this.division === UserDivisions.AUTOS) {
      options.unit = 'mm';
      options.format = [272, 297];
    }
    return options;
  };

  private setValueMultiselect = (formControlName: string, array: CommonDataDTO[]) => {
    if (array?.length > 0) {
      if (array.length === 1) {
        this.searchDataForm.controls[formControlName]?.setValue([array[0]?.id]);
      } else {
        this.searchDataForm.controls[formControlName]?.setValue(array.map((item) => item.id));
      }
    }
  };

  private resetFormControlAndDataList = (formControlName: string) => {
    const formControl = this.searchDataForm.controls[formControlName];
    if (formControl) {
      formControl.reset();
      switch (formControlName) {
        case FORM_PARAM_MACRO_CHAIN:
          this.macroChainList = [];
          break;
        case FORM_PARAM_MACRO_OFFICE:
          this.macroOfficeList = [];
          break;
        case FORM_PARAM_CHAIN:
          this.chainsList = [];
          break;
        case FORM_PARAM_PRESCRIPTOR:
          this.prescriptorsList = [];
          break;
        case FORM_PARAM_ESTABLISHMENT:
          this.establishmentsList = [];
          break;
        default:
          console.error('formControlName - NOT FOUND', formControlName);
      }
    }
  };

  private colsStyle = (pageWidth: number): { [key: string]: Partial<Styles> } => {
    return this.division === UserDivisions.CONSUMPTION
      ? {
          0: { cellWidth: ((pageWidth - 5 * 2) / 10) * 4, halign: 'left' },
          1: { cellWidth: (pageWidth - 5 * 2) / 10, halign: 'center' },
          2: { cellWidth: (pageWidth - 5 * 2) / 10, halign: 'center' },
          3: { cellWidth: (pageWidth - 5 * 2) / 10, halign: 'center' },
          4: { cellWidth: ((pageWidth - 5 * 2) / 10) * 2, halign: 'center' },
          5: { cellWidth: (pageWidth - 5 * 2) / 10, halign: 'center' },
        }
      : this.division === UserDivisions.AUTOS
      ? {
          0: { cellWidth: ((pageWidth - 5 * 2) / 20) * 3, halign: 'left' },
          1: { cellWidth: (pageWidth - 5 * 2) / 20, halign: 'center' },
          2: { cellWidth: (pageWidth - 5 * 2) / 20, halign: 'center' },
          3: { cellWidth: (pageWidth - 5 * 2) / 20, halign: 'center' },
          4: { cellWidth: (pageWidth - 5 * 2) / 20, halign: 'center' },
          5: { cellWidth: (pageWidth - 5 * 2) / 20, halign: 'center' },
          6: { cellWidth: (pageWidth - 5 * 2) / 20, halign: 'center' },
          7: { cellWidth: ((pageWidth - 5 * 2) / 20) * 2, halign: 'center' }, //
          8: { cellWidth: ((pageWidth - 5 * 2) / 20) * 2, halign: 'center' }, //
          9: { cellWidth: ((pageWidth - 5 * 2) / 20) * 2, halign: 'center' }, //
          10: { cellWidth: (pageWidth - 5 * 2) / 20, halign: 'center' },
          11: { cellWidth: (pageWidth - 5 * 2) / 20, halign: 'center' },
          12: { cellWidth: (pageWidth - 5 * 2) / 20, halign: 'center' },
          13: { cellWidth: ((pageWidth - 5 * 2) / 20) * 2, halign: 'center' }, //
        }
      : {};
  };
  private isLoadersCompleted = () =>
    !this.isChainLoading && !this.isPrescriptorLoading && !this.isEstablishmentsLoading;
}
